import { IdGen } from "@base/util/IdGen";
import { PartialRecord } from "@base/util/TypeUtil";
import { OrderType } from "@biz/Definitions";
import { LoungeType } from "@biz/definitions/LoungeDefinitions";
import { FeatureLevel } from "@biz/definitions/ProductDefinitions";
import { ProductVersion } from "@biz/definitions/ProductVersion";
import {
  CornerType, CSPositioning, EdgeName, IEdgeData, IPatchData, ISecurityCheckOptions, ISunSailData, ISunSailRopes, ISvgMeasureOptions, PointName, PoleIndex, PositionMode, PylonType, SailBordersType, StandardTemplateId, StandardTemplateShape,
  SunSailSides, SunSailType, TwistingSetType, WaterDirection
} from "@biz/definitions/SunSailDefinitions";
import { IEditorText } from "@biz/draftEditor/EditorDefinitions";
import { ICurtain2dForm } from "@biz/items/curtain/CurtainDefinitions";
import { RaffType } from "@biz/items/raff/RaffDefinitions";
import { IClothData } from "@biz/logic/ClothData";
import { ShoppingList } from "@biz/models/ShoppingList";
import { ISCPole } from "@biz/sc/sunSail/ISCSunSailData";
import { IProjectOrderInfo } from "@biz/userProjects/new/IProject";
import { ICustomer, IProjectDealer } from "@biz/userProjects/new/IProjectDefinitions";
import { IShoppingList } from "./ShoppingListDefinitions";

export interface IStandardSail {
  id: string;
  shape: StandardTemplateShape;
  sizeId: StandardTemplateId;
  cloth?: IClothData;
  colorId?: string;
  waterDirection?: WaterDirection;
  count: number;
}


export type ISail2dForm = ISail2dAMCXBaseForm | ISail2dCSXSTwinBaseForm | ISail2dFixedForm | ISail2dSandy;
export type IProduct2dForm = ISail2dForm | ICurtain2dForm | ILounge2dForm | IRaff2dForm | IStandardCollection2dForm;
export interface ISail2dProjectForm {
  version: 101;
  dealer: IProjectDealer;
  endCustomer?: ICustomer;
  orderInfo: IProjectOrderInfo;
  sunSail: ISail2dForm;
}

export interface ISail2dProductBaseForm {
  id: string;
  version: ProductVersion;
  orderType: OrderType | undefined;
  count: number;
  cloth?: IClothData;
  colorId?: string;
  shoppingList?: IShoppingList;
  articleSlots: PartialRecord<"revolvingRope" | "rope2" | "motor" | "motorCable", string>,
  notes?: IEditorText;
  featureLevel: FeatureLevel;
}

export interface IRaff2dForm extends ISail2dProductBaseForm {
  productType: "raff2d";
  raffType: RaffType;
}

export interface ILounge2dForm extends ISail2dProductBaseForm {
  productType: "lounge2d";
  loungeType: LoungeType;
}

export type ISail2dObstacles = PartialRecord<SunSailSides, IObstacleData>;
export type SailInputMode = "rectangle" | "default";
export type SailCoilUpType = "clamp" | "snap" | "winch";
export type Sail2dCalculationType = "diagonals" | "distOut";

export interface ISail2dBaseForm extends ISail2dProductBaseForm, ISunSailRopes {
  productType: "sunSail2d";
  raffType: RaffType.None;
  twistingSet?: TwistingSetType;
  highestPoint?: PointName;
  coilUpDeflectionPosition?: PoleIndex;
  coilUpDeflectionOpposite?: "heightChange";
  pylons?: (PylonType | undefined)[];
  alignDistOut?: boolean;
  coilUpPole?: PointName;
  coilUpType?: SailCoilUpType;
  axleType?: ISunSailData["axleType"];
  calculationType?: Sail2dCalculationType;
  positionMode?: PositionMode;
  securityCheckOptions?: ISecurityCheckOptions;
  clothCount?: number;
  waterDirections?: WaterDirection[];
  obstacles?: ISail2dObstacles;
  sailBordersType?: SailBordersType
  edges?: PartialRecord<EdgeName, IEdgeData>;
  cornerTypes?: CornerType[];
  inputMode?: SailInputMode; // SAM
  measureOptions?: Partial<ISvgMeasureOptions>;
  poles: ISCPole[];
  centerPoles?: ISCPole[]; // e.g. Sandy
  patches?: IPatchData[];
}

export interface ISail2dAMCXBaseForm extends ISail2dBaseForm {
  sunSailType: SunSailType.SolidayA | SunSailType.SolidayM | SunSailType.SolidayC | SunSailType.SolidayX | SunSailType.SolidayOne | SunSailType.SolidayFlex;
  poleCount: 3 | 4;
  AC?: number;
  AB?: number;
  BC?: number;
  yAB?: number;
  yCB?: number;
  inOutBPriority: "none" | "out" | "in";
  outB?: number;
  inB?: number;
  AD?: number;
  CD?: number;
  yAD?: number;
  yCD?: number;
  inOutDPriority?: "none" | "out" | "in";
  outD?: number;
  inD?: number;
  outBDPriority?: PointName;
  // mountToAxleC?: number;
}

interface ICSXSData {
  poleCount: number;
  AB?: number;
  BC?: number;
  AC?: number;
  yAC?: number;
  yBC?: number;
  inOutCPriority: "none" | "out" | "in";
  outC?: number;
  inC?: number;
  cs4PositionMode?: CSPositioning[]; // rigid

  // 4
  AD?: number;
  BD?: number;
  outCDPriority?: "C" | "D";

  // 5
  AB2?: number;
  yAE?: number;
  yBE?: number;
  AE?: number;
  BE?: number;
  inOutEPriority?: "none" | "out" | "in";
  outE?: number;
  inE?: number;

  // 6
  AF?: number;
  BF?: number;
  outEFPriority?: "E" | "F";
}

export interface ISail2dCSXSTwinBaseForm extends ISail2dBaseForm, ICSXSData {
  sunSailType: SunSailType.SolidayCS | SunSailType.SolidayXS | SunSailType.SolidayTwin;
}

export interface IStandardCollection2dForm extends ISail2dProductBaseForm {
  productType: "standardCollection2d";
  sails: Record<string, IStandardSail>;
}

export const StandardCollection2dForm = {
  create(featureLevel: FeatureLevel): IStandardCollection2dForm {
    return {
      productType: "standardCollection2d",
      id: IdGen.unsecure.get(),
      count: 1,
      version: "2022",
      featureLevel,
      orderType: "S",
      shoppingList: ShoppingList.createEmpty(),
      articleSlots: {},
      sails: {}
    }
  }
}

// export interface ISail2dStandardForm extends ISail2dBaseForm {
//   sunSailType: SunSailType.Standard;
// }
export interface ISail2dFixedForm extends ISail2dBaseForm {
  poleCount: 3 | 4;
  sunSailType: SunSailType.Fixed;
  AB?: number;
  BC?: number;
  CD?: number;
  AD?: number;
  AC?: number;

  yA?: number;
  yB?: number;
  yC?: number;
  yD?: number;
}
export interface ISail2dSandy extends ISail2dBaseForm {
  sunSailType: SunSailType.SandyPoly | SunSailType.SandyRect;
  poleCount: number;
  dimensions: PartialRecord<EdgeName, number>;
}

export interface IObstacleData {
  side?: SunSailSides;
  m1?: number;
  m2?: number;
  m3?: number;
  m4?: number;
}
